import React, { useState, useEffect, useMemo } from 'react';
import ApiService from '../../services/api';
import { TeacherInfo, TeacherProfile, ClockedInEmployee, ClassroomWithRatio } from '../../types';
import { useStaffStatus } from '../../contexts/StaffStatusContext';
import './NotCheckedInEmployees.css';

import * as ApiTypes from '../../types/api';
import { useClassroomData, processedClassroomData } from '../../data/classroomData';
import { useStaffData } from '../../data/staffData';

interface NotCheckedInEmployeesProps {
  title?: string;
  refreshTrigger?: number; // Changed from refreshInterval to refreshTrigger
}

// New interface for our consolidated state
interface NotCheckedInEmployee extends ClockedInEmployee {
  smartcareProfile: TeacherProfile | null;
}

const NotCheckedInEmployees: React.FC<NotCheckedInEmployeesProps> = ({ 
  title = "Missing Smartcare Checkins",
  refreshTrigger, // Changed from refreshInterval
}) => {
  // Get classrooms from context
  const classroomData = useClassroomData().data;
  const { classroomOptions: classrooms } = useMemo(()=> {return processedClassroomData(classroomData)},[classroomData]);

  const { setMissingCheckins } = useStaffStatus();
  
  const [notCheckedInEmployees, setNotCheckedInEmployees] = useState<NotCheckedInEmployee[]>([]);
  
  // Check-in dialog state
  const [checkingIn, setCheckingIn] = useState<Record<string, boolean>>({});
  const [selectedClassroom, setSelectedClassroom] = useState<Record<string, string>>({});
  const [activeDialogEmployeeId, setActiveDialogEmployeeId] = useState<number | null>(null);
  const [checkInErrors, setCheckInErrors] = useState<Record<string, string>>({});

  const staffData = useStaffData().data
  const loading = useStaffData().isLoading
  const smartcareCheckedInStaff = useMemo (() => staffData?.smartcareCheckedInStaff ?? [], [staffData]);
  const wheniworkClockedinStaff = useMemo (() => staffData?.wheniworkClockedinStaff ?? [], [staffData]);
  const smartcareStaffProfiles  = useMemo (() => staffData?.smartcareStaffProfiles ?? [], [staffData]);

  // Process data to find staff not checked in to SmartCare and find their matching profiles
  useEffect(() => {
    // Step 1: Filter to find staff clocked in on WhenIWork but not checked in on SmartCare
    const notCheckedIn = wheniworkClockedinStaff.filter(employee => 
      !smartcareCheckedInStaff.some(({ profile:smartcareProfile, employee: smartcareEmployee }) => 
        employee.employee_code === smartcareProfile.customId ||
        (employee.first_name === smartcareProfile.firstName && employee.last_name === smartcareProfile.lastName)
      )
    );

    // Step 2: Find matching SmartCare profiles for each employee not checked in
    const employeesWithProfiles = notCheckedIn.map(wheniworkEmployee => {
      // Try to find a matching teacher profile by employee_code or name
      const matchingProfile = smartcareStaffProfiles.find(smartcareProfile => 
        smartcareProfile.customId === wheniworkEmployee.employee_code || 
        (smartcareProfile.firstName === wheniworkEmployee.first_name && smartcareProfile.lastName === wheniworkEmployee.last_name)
      ) || null;
      
      // If we found a match and employee has a position, try to pre-select matching classroom
      if (matchingProfile && wheniworkEmployee.position) {
        const wheniworkPositionName = wheniworkEmployee.position.name?.toLowerCase();
        if (wheniworkPositionName) {
          // Try to find a classroom that matches position name
          const matchingClassroom = classrooms.find(classroom => 
            wheniworkPositionName.includes(classroom.name.toLowerCase()) || 
            classroom.name.toLowerCase().includes(wheniworkPositionName)
          );
          
          if (matchingClassroom) {
            setSelectedClassroom(prev => ({
              ...prev,
              [wheniworkEmployee.id]: matchingClassroom.id
            }));
          } else if (matchingProfile.className) {
            // Default to classroom associated with teacher smartcare profile if no position match
            setSelectedClassroom(prev => ({
              ...prev,
              [wheniworkEmployee.id]: matchingProfile.className
            }));
          } else if (classrooms.length > 0) {
            // Default to first available classroom as fallback
            setSelectedClassroom(prev => ({
              ...prev,
              [wheniworkEmployee.id]: classrooms[0].name
            }));
          }
        }
      }
      
      // Return the employee with its matching SmartCare profile
      return {
        ...wheniworkEmployee,
        smartcareProfile: matchingProfile
      };
    });
    
    setNotCheckedInEmployees(employeesWithProfiles);
    
    // Update the context with the count of missing checkins
    setMissingCheckins(employeesWithProfiles.length);
  }, [smartcareCheckedInStaff, wheniworkClockedinStaff, smartcareStaffProfiles, classrooms, setMissingCheckins]);

  // Handle opening the check-in dialog
  const handleOpenCheckInDialog = (employeeId: number) => {
    setActiveDialogEmployeeId(employeeId);
  };

  // Handle closing the check-in dialog
  const handleCloseCheckInDialog = () => {
    setActiveDialogEmployeeId(null);
  };

  // Handle classroom selection change
  const handleClassroomChange = (employeeId: number, classroomId: string) => {
    setSelectedClassroom(prev => ({ ...prev, [employeeId]: classroomId }));
  };

  // Handle check-in submission
  const handleCheckIn = async (employeeId: number) => {
    const employee = notCheckedInEmployees.find(emp => emp.id === employeeId);
    const matchingTeacher = employee?.smartcareProfile;
    const classroomId = selectedClassroom[employeeId];
    
    handleCloseCheckInDialog();

    if (!matchingTeacher || !classroomId) {
      setCheckInErrors(prev => ({ 
        ...prev, 
        [employeeId]: 'Missing teacher match or classroom selection' 
      }));
      return;
    }
    
    try {
      // Set loading state for this employee
      setCheckingIn(prev => ({ ...prev, [employeeId]: true }));
      
      // Call API to check in teacher
      const result = await ApiService.checkInTeacher({
        teacherId: matchingTeacher.guid,
        classroomId: classroomId
      });
      
      if (result.success) {
        // Remove from not checked in list - this is all we need
        setNotCheckedInEmployees(prev => 
          prev.filter(employee => employee.id !== employeeId)
        );
        
        // Clear any previous error
        setCheckInErrors(prev => {
          const updated = { ...prev };
          delete updated[employeeId];
          return updated;
        });
      } else {
        // Set error for this employee
        setCheckInErrors(prev => ({ 
          ...prev, 
          [employeeId]: result.message || 'Failed to check in employee' 
        }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error checking in employee';
      
      // Set error for this employee
      setCheckInErrors(prev => ({ 
        ...prev, 
        [employeeId]: errorMessage 
      }));
    } finally {
      // Clear loading state
      setCheckingIn(prev => {
        const updated = { ...prev };
        delete updated[employeeId];
        return updated;
      });

      // Close dialog
      handleCloseCheckInDialog(); 
    }
  };

  const error = useStaffData().failureReason;

  return (
    <div className="not-checked-in-employees-component employee-container">
      <h2>{title}</h2>
      
      {loading && <div className="loading-indicator">Loading employee data...</div>}
      
      {error && <div className="error-message">Error: {error.message}</div>}
      
      {!loading && !error && notCheckedInEmployees.length === 0 && (
        <div className="empty-state">All employees are properly checked in</div>
      )}
      
      {notCheckedInEmployees.length > 0 && (
        <div className="employee-list">
          <ul className="employee-items">
            {notCheckedInEmployees.map(employee => (
              <li key={employee.id} className="employee-item">
                <div className="employee-details">
                  <div className="employee-name">
                    {employee.first_name} {employee.last_name}
                  </div>
                  {employee.position && employee.position.name && (
                    <div className="employee-location">
                      Position: {employee.position.name}
                    </div>
                  )}
                  {employee.employee_code && (
                    <div className="employee-id">
                      Employee ID: {employee.employee_code}
                    </div>
                  )}
                </div>
                
                <div className="action-area">
                  {employee.smartcareProfile ? (
                    <>
                      <button
                        className="check-in-button"
                        onClick={() => handleOpenCheckInDialog(employee.id)}
                        disabled={checkingIn[employee.id] || false}
                      >
                        {checkingIn[employee.id] ? 'Checking in...' : 'Check In to SmartCare'}
                      </button>
                      
                      {activeDialogEmployeeId === employee.id && (
                        <div className="dialog-overlay">
                          <div className="dialog-box">
                            <h3>Check In {employee.first_name} {employee.last_name}</h3>
                            <p>Select classroom to check into:</p>
                            
                            <select 
                              value={selectedClassroom[employee.id] || ''}
                              onChange={(e) => handleClassroomChange(employee.id, e.target.value)}
                              className="classroom-select"
                            >
                              <option value="">-- Select Classroom --</option>
                              {classrooms.map(classroom => (
                                <option key={classroom.id} value={classroom.id}>
                                  {classroom.name}
                                </option>
                              ))}
                            </select>
                            
                            <div className="dialog-buttons">
                              <button 
                                className="dialog-cancel" 
                                onClick={() => handleCloseCheckInDialog()}
                              >
                                Cancel
                              </button>
                              <button 
                                className="dialog-confirm" 
                                onClick={() => handleCheckIn(employee.id)}
                                disabled={!selectedClassroom[employee.id]}
                              >
                                Check In
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <button className="check-in-button disabled" disabled>
                      No Match in SmartCare
                    </button>
                  )}
                  
                  {checkInErrors[employee.id] && (
                    <div className="error-message">
                      {checkInErrors[employee.id]}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotCheckedInEmployees;
